import {forkJoin, from, Observable, of, ReplaySubject, throwError} from 'rxjs';
import {map, mergeMap, tap} from 'rxjs/operators';
import Keycloak, {KeycloakInitOptions, KeycloakProfile} from 'keycloak-js';
import {ClassTransformer, Expose, Transform} from 'class-transformer';
import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {GlobalPermissionId, PermissionId} from '@app/layout/admin/services/api/permission-interfaces';
import {BaseUserService} from './base-user.service';
import {UsageAnalyticsService} from '../usage-analytics/usage-analytics.service';
import {WebBrowserCommunicationService} from "@shared/services/web-browser/web-browser-communication.service";
import {APIClientEntity, APIContractorEntity, APICustomerEntity, CustomerId, UserEntityType, BaseUserEntity, UserEntity, UserEntityId} from '@app/shared/services/api/user-entity';
import {KeycloakLoggedInUser, License, LicenseProperties, LoggedInUser, PermissionedUserEntity} from './logged-in-user';
import {LoggedInUserState} from '@shared/services/api/user/logged-in-user-state';
import {UserPreferences, UserPreferenceService} from '@shared/services/users/user-preference.service';
import {HttpUtilityService} from "@shared/services/api/http-utility-service";
import {FrontendDomainConfig} from './dashboard-config.interface';
import {CustomizationProperty} from "@shared/services/api/customization-property";
import {CustomizationPropertyName} from "@app/layout/admin/services/api/customer-customization-property.interface";
import {CUSTOMIZATION_PROPERTY_TYPES_BY_NAME} from "@app/layout/admin/services/api/customer-interfaces";
import {LoginRedirectService} from "@shared/services/routing/login-redirect.service";


interface UserData {
  preferences: UserPreferences;
  permissions: UserPermissions;
  license: License;
  overrideUserEntity?: BaseUserEntity;
}


interface APIUserEntityCustomerNamePart {
  customer_name: string;
}


type CurrentUserDataClientEntity = APIClientEntity & APIUserEntityCustomerNamePart;
type CurrentUserDataContractorEntity = APIContractorEntity & APIUserEntityCustomerNamePart;


interface APICurrentUserData {
  global_permissions: GlobalPermissionId[];
  customer?: APICustomerEntity;
  client?: CurrentUserDataClientEntity;
  contractor?: CurrentUserDataContractorEntity;
  license: LicenseProperties;
  preferences: UserPreferences;
  override_user_entity?: BaseUserEntity;
}


interface UsageAnalyticsIdentityTraits {
  username: string;
  name: string;
  email: string;
  userentity?: string;
  userentity_name?: string;
  customer_entity?: string;
  customer_entity_name?: string;
  realuserentity?: string;
  realuserentity_name?: string;
  real_customer_entity?: string;
  real_customer_entity_name?: string;
}


export class UserEntityImpl implements UserEntity {
  id: UserEntityId;
  name: string;
  type: UserEntityType;

  customer_id?: CustomerId;
  customer_name?: string;

  @Expose({name: 'logo_url'})
  @Transform(({value}) => value ? new URL(value) : value, {toClassOnly: true})
  @Transform(({value}) => value ? value.toString() : value, {toPlainOnly: true})
  logoUrl?: URL;

  @Expose({name: 'customer_logo_url'})
  @Transform(({value}) => value ? new URL(value) : value, {toClassOnly: true})
  @Transform(({value}) => value ? value.toString() : value, {toPlainOnly: true})
  customerLogoUrl?: URL;

  @Expose({name: 'customer_customization_properties'})
  @Transform(({value: properties}) => CustomizationProperty.toMap(properties, CUSTOMIZATION_PROPERTY_TYPES_BY_NAME), {toClassOnly: true})
  customerCustomizationProperties?: Map<string, any>;

  getWebsiteUrl(): string | null {
    return this.customerCustomizationProperties?.get(CustomizationPropertyName.WebsiteUrl) ?? null;
  }

  getHideBranding(): boolean {
    return this.customerCustomizationProperties?.get(CustomizationPropertyName.HideBranding) ?? false;
  }

  getHideIntegrations(): boolean {
    return this.customerCustomizationProperties?.get(CustomizationPropertyName.HideIntegrations) ?? false;
  }
}



@Injectable()
export class UserProfileService extends BaseUserService {

  private readonly logger: AceLogger;

  private headers: HttpHeaders = this.getHeaders();
  private authenticatedUserSubject = new ReplaySubject<LoggedInUser>();
  readonly authenticatedUser$ = this.authenticatedUserSubject.asObservable();
  private userProfile: KeycloakProfile | null = null;
  private domainConfig: FrontendDomainConfig | null;
  private isUsageAnalyticsSetupDone = false;

  constructor(private http: HttpClient, private keycloak: Keycloak, private classTransformer: ClassTransformer,
              private loggedInUserState: LoggedInUserState, private userPreferencesService: UserPreferenceService,
              private usageAnalyticsService: UsageAnalyticsService, private browserCommsService: WebBrowserCommunicationService,
              private httpUtilityService: HttpUtilityService, private loginRedirectService: LoginRedirectService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(UserProfileService.name);
  }

  init(options: KeycloakInitOptions, domainConfig: FrontendDomainConfig | null): Observable<boolean> {
    this.domainConfig = domainConfig;
    // On refresh failure keycloak-js only redirects to Keycloak by itself when the page was initialised with login-required.
    // Every login now lands on /login-redirect, which is initialised with check-sso, so when the token expires and cannot be
    // refreshed the app has to trigger that redirect itself.
    this.keycloak.onAuthLogout = () => this.loginAgainAfterSessionExpiry();
    const redirectUri = this.loginRedirectService.buildRedirectUriForCurrentUrl();
    return from(this.keycloak.init({...options, ...redirectUri && {redirectUri}})).pipe(
      mergeMap(authenticated => {
        if (authenticated) {
          return this.refreshLoggedInUser(true).pipe(map(() => authenticated));
        } else {
          return of(authenticated);
        }
      }),
      tap((authenticated) => this.authenticatedUserSubject.next(authenticated ? this.getLoggedInUser() : null))
    );
  }

  public refreshLoggedInUser(useCache = false): Observable<KeycloakLoggedInUser> {
    const userId = this.keycloak.tokenParsed.sub;
    return forkJoin([
      this.getCurrentUsersData(useCache),
      this.loadUserProfile()
    ]).pipe(
      map(([currentUserData, keycloakProfile]) => new KeycloakLoggedInUser(
        userId,
        currentUserData.permissions.entity,
        currentUserData.license,
        currentUserData.preferences,
        currentUserData.overrideUserEntity ?? null,
        this.getLoggedInUser()?.nonPersistedPreferences,
        keycloakProfile,
        currentUserData.permissions.global_permissions,
        this.classTransformer)
      ),
      tap(loggedInUser => {
        this.setupUsageAnalytics(loggedInUser);
        this.loggedInUserState.setLoggedInUser(loggedInUser);
      })
    );
  }

  private getCustomerEntityIdName(userEntity: BaseUserEntity | null): { id: CustomerId, name: string } | null {
    if (userEntity) {
      if (userEntity.type === UserEntityType.CUSTOMER) {
        return {
          id: userEntity.id,
          name: userEntity.name
        };
      } else {
        return {
          id: userEntity.customer_id,
          name: userEntity.customer_name
        };
      }
    } else {
      return null;
    }
  }

  private setupUsageAnalytics(loggedInUser: KeycloakLoggedInUser) {
    if (!this.isUsageAnalyticsSetupDone) {
      const userId = loggedInUser.userId;
      const username = loggedInUser.username;
      const fullName = loggedInUser.firstName + ' ' + loggedInUser.lastName;
      const email = loggedInUser.email;
      const rawUserEntity = loggedInUser.userEntity;
      const userEntity = loggedInUser.overrideUserEntity ?? loggedInUser.userEntity;
      const customerEntity = this.getCustomerEntityIdName(userEntity);
      const rawCustomerEntity = this.getCustomerEntityIdName(rawUserEntity);
      const identityTraits: UsageAnalyticsIdentityTraits = {
        username,
        name: fullName,
        email,
        ...userEntity && {
          userentity: userEntity.type + ':' + userEntity.id,
          userentity_name: userEntity.name
        },
        ...customerEntity && {
          customer_entity: 'customer:' + customerEntity.id,
          customer_entity_name: customerEntity.name
        },
        ...rawUserEntity && {
          realuserentity: rawUserEntity.type + ':' + rawUserEntity.id,
          realuserentity_name: rawUserEntity.name
        },
        ...rawCustomerEntity && {
          real_customer_entity: 'customer:' + rawCustomerEntity.id,
          real_customer_entity_name: rawCustomerEntity.name
        }
      };

      this.usageAnalyticsService.alias(userId);
      this.usageAnalyticsService.identify(userId, identityTraits);
      this.usageAnalyticsService.track('Login', {username});

      if (userEntity) {
        this.usageAnalyticsService.group(identityTraits.userentity, {
          id: userEntity.id,
          name: userEntity.name,
          type: userEntity.type,
          customer_id: customerEntity.id,
          customer_name: customerEntity.name
        });
      }
      this.isUsageAnalyticsSetupDone = true;
    }
  }

  private getCurrentUsersData(useCache: boolean): Observable<UserData> {
    const userData: UserData = useCache ? this.browserCommsService.getFromParent(WebBrowserCommunicationService.KEYS.USER_DATA) : null;
    if (userData == null) {
      this.logger.debug('Fetching user data');
      const options = {headers: this.headers};
      return this.http.get(this.getGateway() + '/users/current-user-data', options).pipe(map(response => {
        const {customer, client, contractor, license, global_permissions, preferences, override_user_entity}: APICurrentUserData =
          this.httpUtilityService.extractResponseDataMultiKeyCompat(response, 'current_user_data');
        const permissions: UserPermissions = <UserPermissions>{global_permissions: global_permissions ?? []};
        if (customer) {
          permissions.entity = this.classTransformer.plainToInstance(UserEntityImpl, {
            type: UserEntityType.CUSTOMER,
            id: customer.customer_id,
            name: customer.customer_name,
            customer_id: customer.customer_id,
            customer_name: customer.customer_name,
            logo_url: customer.logo_url,
            permissions: customer.permissions ? customer.permissions : [],
            customer_customization_properties: customer.customer_customization_properties
          });
        } else if (client) {
          permissions.entity = this.classTransformer.plainToInstance(UserEntityImpl, {
            type: UserEntityType.CLIENT,
            id: client.client_id,
            name: client.client_name,
            logo_url: client.logo_url,
            customer_id: client.customer_id,
            customer_name: client.customer_name,
            customer_logo_url: client.customer_logo_url,
            permissions: client.permissions ? client.permissions : [],
            customer_customization_properties: client.customer_customization_properties ?? []
          });
        } else if (contractor) {
          permissions.entity = this.classTransformer.plainToInstance(UserEntityImpl, {
            type: UserEntityType.CONTRACTOR,
            id: contractor.contractor_id,
            name: contractor.contractor_name,
            logo_url: contractor.logo_url,
            customer_id: contractor.customer_id,
            customer_name: contractor.customer_name,
            customer_logo_url: contractor.customer_logo_url,
            permissions: contractor.permissions ? contractor.permissions : [],
            customer_customization_properties: contractor.customer_customization_properties ?? []
          });
        }

        const result = {
          preferences: this.userPreferencesService.mergeWithDefaultPreferences(preferences),
          permissions: permissions,
          license: (license ? new LicenseImpl(license) : null),
          ...override_user_entity && {overrideUserEntity: override_user_entity}
        };

        this.browserCommsService.update(WebBrowserCommunicationService.KEYS.USER_DATA, result);

        return result;
      }));
    } else {
      this.logger.debug('Using cached user data from parent browser window');
      return of(userData);
    }
  }

  isIntegrationsEnabled(): boolean {
    const userEntity = this.getLoggedInUser()?.userEntity;
    if (userEntity) {
      return !userEntity.getHideIntegrations();
    } else {
      return false;
    }
  }

  getLoggedInUser(): LoggedInUser {
    return this.loggedInUserState.getLoggedInUser();
  }

  createAccountUrl(): string {
    return this.keycloak.createAccountUrl();
  }

  /** targetUrl is where the browser lands once logged in, as an app URL relative to the base href. */
  login(targetUrl: string): Promise<void> {
    return this.loginWithRedirectUri(this.loginRedirectService.buildRedirectUri(targetUrl));
  }

  private trackLogoutEvent() {
    const username = this.keycloak.profile?.username;
    if (!username) {
      this.logger.error('Cannot track user logout event as user profile is not available');
    } else {
      this.usageAnalyticsService.track('Logout', { username });
    }
  }

  logout(redirectUri?: string): Promise<any> {
    // Call before clearing the user to ensure the Logout Event is tracked against the logged-in user
    this.trackLogoutEvent();
    const loggedInUser = this.loggedInUserState.getLoggedInUser();
    const logoutRedirectUri = loggedInUser?.userEntity?.getWebsiteUrl() ?? redirectUri;
    this.loggedInUserState.clearLoggedInUser();
    this.authenticatedUserSubject.next(null);
    this.userProfile = null;
    this.isUsageAnalyticsSetupDone = false;
    return this.keycloak.logout({...logoutRedirectUri && {redirectUri: logoutRedirectUri}});
  }

  updateCurrentUserRowsPerPagePreferences(rowsPerPage: number): Observable<Object> {
    const loggedInUser = this.loggedInUserState.getLoggedInUser();
    if (loggedInUser) {
      loggedInUser.preferences.table_options.rows_per_page = rowsPerPage;
      return this.userPreferencesService.updateUserPreferences(loggedInUser.userId, loggedInUser.preferences);
    } else {
      return of(null);
    }
  }

  updateActivityContainerHeightUserPreference(height: number): void {
    const user = this.getLoggedInUser();
    if (user) {
      if (!user.nonPersistedPreferences) {
        user.nonPersistedPreferences = {tickets: {view: {activityContainerHeight: null}}};
      } else if (!user.nonPersistedPreferences.tickets) {
        user.nonPersistedPreferences.tickets = {view: {activityContainerHeight: null}};
      } else if (!user.nonPersistedPreferences.tickets.view) {
        user.nonPersistedPreferences.tickets.view = {activityContainerHeight: null};
      }
      user.nonPersistedPreferences.tickets.view.activityContainerHeight = height != null ? height : null;
    }
  }

  public isPlatformAdminOrCustomerUser(user: LoggedInUser): boolean {
    return user && (user.isPlatformAdmin() || user.isCustomerUser());
  }

  public isPlatformAdminOrCustomerAdmin(user: LoggedInUser): boolean {
    return user && (user.isPlatformAdmin() || user.hasPermissionOnUserEntity(PermissionId.CUSTOMER_ADMIN));
  }

  private loadUserProfile(): Observable<KeycloakProfile> {
    const keycloakProfile$ = !this.userProfile
      ? from(this.keycloak.loadUserProfile())
      : of(this.userProfile);
    return keycloakProfile$.pipe(mergeMap(keyCloakProfile =>
      this.keycloak.authenticated
        ? of(keyCloakProfile)
        : throwError(() => new Error('The user profile was not loaded as the user is not logged in.'))
    ));
  }

  private loginWithRedirectUri(redirectUri: string | null): Promise<void> {
    const idpHint = this.domainConfig?.sso?.keycloak?.idp_hint;
    return this.keycloak.login({
      ...redirectUri && {redirectUri},
      ...idpHint && {idpHint}
    });
  }

  private loginAgainAfterSessionExpiry(): void {
    this.logger.info('The Keycloak session has expired, logging in again');
    this.loginWithRedirectUri(this.loginRedirectService.buildRedirectUriForCurrentUrl());
  }
}

interface UserPermissions {
  global_permissions: GlobalPermissionId[];
  entity: PermissionedUserEntity;
}

class LicenseImpl implements License {
  energy_module: boolean;
  maintenance_module: boolean;
  thermal_comfort_module: boolean;

  constructor(l: LicenseProperties) {
    this.energy_module = l.energy_module;
    this.thermal_comfort_module = l.thermal_comfort_module;
    this.maintenance_module = l.maintenance_module;
  }

  isLicensed(): boolean {
    return this.energy_module || this.thermal_comfort_module || this.maintenance_module;
  }
}
